import { useState, useEffect } from "react";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";

export const ControlPanel = ({ config, onConfig, extra }) => {
  const [risk, setRisk] = useState(1);
  const [dloss, setDloss] = useState(3);
  const [thr, setThr] = useState(6);
  const [maxpos, setMaxpos] = useState(2);

  useEffect(() => {
    if (!config) return;
    setRisk(Math.round(config.risk_per_trade * 1000) / 10);
    setDloss(Math.round(config.max_daily_loss * 1000) / 10);
    setThr(config.confluence_threshold);
    setMaxpos(config.max_positions);
    // depend on the PRIMITIVE values (not the config object, which is a new reference on every
    // WS state push) so a live broadcast can't clobber a slider the user is mid-adjusting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.risk_per_trade, config?.max_daily_loss, config?.confluence_threshold, config?.max_positions]);

  if (!config) return null;
  const symbols = Object.keys(config.enabled_symbols || {});

  const Row = ({ label, value, children }) => (
    <div className="px-3 py-2 border-b border-zinc-800/70">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</span>
        <span className="font-mono text-[11px] font-semibold text-blue-400">{value}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col overflow-auto thin-scroll">
      <div className="px-3 py-2 border-b border-zinc-800">
        <div className="text-[9px] uppercase tracking-wide text-zinc-500 mb-1.5">Symbols</div>
        <div className="flex flex-col gap-1.5">
          {symbols.map((s) => (
            <div key={s} className="flex items-center justify-between">
              <span className="font-mono text-[12px] font-semibold text-zinc-200">{s}</span>
              <Switch
                data-testid={`symbol-toggle-${s}`}
                checked={config.enabled_symbols[s]}
                onCheckedChange={(v) =>
                  onConfig({ enabled_symbols: { ...config.enabled_symbols, [s]: v } })
                }
                className="data-[state=checked]:bg-green-500"
              />
            </div>
          ))}
        </div>
      </div>

      <Row label="Risk / Trade" value={`${risk}%`}>
        <Slider
          data-testid="risk-slider"
          min={0.5} max={5} step={0.5} value={[risk]}
          onValueChange={(v) => setRisk(v[0])}
          onValueCommit={(v) => onConfig({ risk_per_trade: v[0] / 100 })}
        />
      </Row>
      <Row label="Max Daily Loss" value={`${dloss}%`}>
        <Slider
          data-testid="daily-loss-slider"
          min={1} max={10} step={0.5} value={[dloss]}
          onValueChange={(v) => setDloss(v[0])}
          onValueCommit={(v) => onConfig({ max_daily_loss: v[0] / 100 })}
        />
      </Row>
      <Row label="Confluence Threshold" value={`${thr} / 11`}>
        <Slider
          data-testid="threshold-slider"
          min={2} max={11} step={1} value={[thr]}
          onValueChange={(v) => setThr(v[0])}
          onValueCommit={(v) => onConfig({ confluence_threshold: v[0] })}
        />
      </Row>
      <Row label="Max Positions" value={maxpos}>
        <Slider
          data-testid="maxpos-slider"
          min={1} max={5} step={1} value={[maxpos]}
          onValueChange={(v) => setMaxpos(v[0])}
          onValueCommit={(v) => onConfig({ max_positions: v[0] })}
        />
      </Row>
      <div className="px-3 py-2.5 border-b border-zinc-800/70 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold text-zinc-200">Chop Mode</div>
          <div className="text-[9px] text-zinc-500 leading-tight">Trade wedge rails with the 1H trend</div>
        </div>
        <Switch
          data-testid="chop-mode-switch"
          checked={config.chop_mode !== false}
          onCheckedChange={(v) => onConfig({ chop_mode: v })}
        />
      </div>
      <div className="px-3 py-2.5 border-b border-zinc-800/70 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold text-zinc-200">Allow Stacking</div>
          <div className="text-[9px] text-zinc-500 leading-tight">Pyramid same-direction adds into a winning position</div>
        </div>
        <Switch
          data-testid="allow-stacking-switch"
          checked={!!config.allow_stacking}
          onCheckedChange={(v) => onConfig({ allow_stacking: v })}
          className="data-[state=checked]:bg-amber-500"
        />
      </div>
      {config.allow_stacking && (
        <Row label="Max Stack (adds)" value={config.max_stack ?? 3}>
          <Slider
            data-testid="maxstack-slider"
            min={1} max={6} step={1} value={[config.max_stack ?? 3]}
            onValueCommit={(v) => onConfig({ max_stack: v[0] })}
          />
        </Row>
      )}
      {extra}
    </div>
  );
};
